import { FC, FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { formatDateTime } from '@/shared/lib/format';
import AppButton from '@/shared/ui/AppButton';

import { useOrderDriveMutations } from '../../api/useArtworkDriveMutations';
import { useDriveFolderAccessQuery } from '../../api/useArtworkDriveQueries';
import { resolveDriveError } from '../../model/driveErrors';
import { DriveFolderRef, isShareableEmail, shareRoleFor } from '../../model/driveFolders';
import { DriveAccessRow } from '../../model/types';

interface Props {
  open: boolean;
  onClose: () => void;
  orderId: string;
  folder: DriveFolderRef;
  title: string;
}

/**
 * Who reaches one folder, and why. Staff see every person; an advertiser sees their own people and the hand shares,
 * and every other side as a head count. A hand share is made on fixed terms: a Viewer on a booking or decoration, a
 * Contributor on the drop. An address with no Google account is invited as a visitor and stays a Viewer.
 */
const DriveAccessDialog: FC<Props> = ({ open, onClose, orderId, folder, title }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const access = useDriveFolderAccessQuery(orderId, folder, open);
  const { share, unshare } = useOrderDriveMutations(orderId);
  const shareRole = shareRoleFor(folder);
  const emailValid = isShareableEmail(email);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!shareRole || !emailValid) return;
    share.mutate(
      { folder, request: { email: email.trim(), role: shareRole } },
      { onSuccess: () => setEmail('') },
    );
  };

  const rowText = (row: DriveAccessRow) => ({
    primary:
      row.email ??
      t('artwork_drive.access.people', {
        count: row.count,
        side: t(`artwork_drive.side.${row.side}`),
      }),
    secondary: [
      t(`artwork_drive.side.${row.side}`),
      t(`artwork_drive.role.${row.role}`),
      row.inheritedFrom ? t('artwork_drive.access.inherited') : undefined,
      row.expiresAt
        ? t('artwork_drive.access.expires', { time: formatDateTime(row.expiresAt) })
        : undefined,
    ]
      .filter(Boolean)
      .join(' · '),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" data-testid="drive-access-dialog">
      <DialogTitle>{t('artwork_drive.access.title', { folder: title })}</DialogTitle>
      <DialogContent>
        {access.isLoading ? <CircularProgress size={24} /> : null}
        {access.isError ? (
          <Alert severity="error">{resolveDriveError(access.error, t)}</Alert>
        ) : null}
        {access.data ? (
          <List dense disablePadding>
            {access.data.map((row) => {
              const text = rowText(row);

              return (
                <ListItem
                  key={row.permissionId ?? `${row.side}-${row.role}`}
                  disableGutters
                  secondaryAction={
                    row.removable && row.permissionId ? (
                      <Tooltip title={t('artwork_drive.access.remove')}>
                        <span>
                          <IconButton
                            edge="end"
                            size="small"
                            aria-label={t('artwork_drive.access.remove')}
                            disabled={unshare.isLoading}
                            onClick={() =>
                              unshare.mutate({ folder, permissionId: row.permissionId as string })
                            }
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : undefined
                  }
                >
                  <ListItemText primary={text.primary} secondary={text.secondary} />
                </ListItem>
              );
            })}
          </List>
        ) : null}
        {shareRole ? (
          <Stack component="form" onSubmit={submit} gap={1} mt={2}>
            <Typography variant="subtitle2">{t('artwork_drive.access.share_title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t(`artwork_drive.access.share_terms_${shareRole}`)}
            </Typography>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                type="email"
                label={t('artwork_drive.access.email')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={email.length > 0 && !emailValid}
                inputProps={{ 'data-testid': 'drive-share-email' }}
              />
              <AppButton type="submit" variant="primary" disabled={!emailValid || share.isLoading}>
                {t('artwork_drive.access.share')}
              </AppButton>
            </Stack>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <AppButton variant="secondary" onClick={onClose}>
          {t('buttons.close')}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default DriveAccessDialog;
